import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Eye, EyeOff, ToggleLeft, ToggleRight, Loader2, UserCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const AdminTestAccounts = () => {
  const { api } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAccount, setNewAccount] = useState({ username: '', password: '', email: '' });
  const [showPassword, setShowPassword] = useState(false);

  const fetchAccounts = async () => {
    try {
      const { data } = await api.get('/admin/test-accounts');
      setAccounts(data.accounts || []);
    } catch (e) {
      toast.error('Failed to load test accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(); }, []);

  const handleCreate = async () => {
    if (!newAccount.username || !newAccount.password) {
      toast.error('Username and password are required');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/admin/test-accounts', newAccount);
      toast.success('Test account created');
      setShowCreateDialog(false);
      setNewAccount({ username: '', password: '', email: '' });
      fetchAccounts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create account');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (account) => {
    try {
      await api.patch(`/admin/test-accounts/${account.id}`, { is_active: !account.is_active });
      toast.success(`Account ${account.is_active ? 'disabled' : 'enabled'}`);
      fetchAccounts();
    } catch (e) {
      toast.error('Failed to update account');
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Delete test account "${account.username}"?`)) return;
    
    try {
      await api.delete(`/admin/test-accounts/${account.id}`);
      toast.success('Test account deleted');
      fetchAccounts();
    } catch (e) {
      toast.error('Failed to delete account');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Test Accounts</h1>
          <p className="text-slate-500">Manage test accounts for Razorpay website verification</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="bg-teal-500 hover:bg-teal-600">
          <Plus className="w-4 h-4 mr-2" /> Create Account
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <UserCheck className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">For Razorpay Verification</h3>
              <p className="text-sm text-blue-600 mt-1">
                Create test accounts here for Razorpay's website verification process. 
                Share these credentials with Razorpay to get your live API keys.
              </p>
              <p className="text-sm text-blue-600 mt-2">
                <strong>Login URL:</strong>{' '}
                <code className="bg-blue-100 px-2 py-0.5 rounded">{window.location.origin}/test-login</code>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accounts List */}
      {accounts.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No test accounts created yet</p>
            <Button 
              onClick={() => setShowCreateDialog(true)} 
              variant="outline" 
              className="mt-4"
            >
              Create First Account
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {accounts.map(account => (
            <Card key={account.id} className="border-slate-200">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${account.is_active ? 'bg-teal-100' : 'bg-slate-100'}`}>
                      <UserCheck className={`w-5 h-5 ${account.is_active ? 'text-teal-600' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{account.username}</span>
                        <Badge className={account.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
                          {account.is_active ? 'Active' : 'Disabled'}
                        </Badge>
                      </div>
                      {account.email && (
                        <p className="text-sm text-slate-500">{account.email}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggle(account)}
                      className="text-slate-600"
                    >
                      {account.is_active ? (
                        <ToggleRight className="w-5 h-5 text-teal-500" />
                      ) : (
                        <ToggleLeft className="w-5 h-5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(account)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Test Account</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Username *</Label>
              <Input
                value={newAccount.username}
                onChange={(e) => setNewAccount({ ...newAccount, username: e.target.value })}
                placeholder="test_user"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Password *</Label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={newAccount.password}
                  onChange={(e) => setNewAccount({ ...newAccount, password: e.target.value })}
                  placeholder="••••••••"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email (optional)</Label>
              <Input
                type="email"
                value={newAccount.email}
                onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                placeholder="test@example.com"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating} className="bg-teal-500 hover:bg-teal-600">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Create Account
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTestAccounts;
